import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { GBP } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { withOverride } from "@/lib/override";
import { toast } from "sonner";
import { Check, X, UserX, Trash2, Clock } from "lucide-react";

const STATUS_ACTIONS = [
  { status: "confirmed", label: "Confirm", icon: Check, cls: "text-emerald-700 border-emerald-200 hover:bg-emerald-50" },
  { status: "completed", label: "Complete", icon: Check, cls: "text-stone-700 border-stone-200 hover:bg-stone-50" },
  { status: "no_show", label: "No-show", icon: UserX, cls: "text-purple-700 border-purple-200 hover:bg-purple-50" },
  { status: "cancelled", label: "Cancel", icon: X, cls: "text-rose-700 border-rose-200 hover:bg-rose-50" },
];

export default function AppointmentDialog({ open, onClose, onSaved, initial, services, staff, clients }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({});
  const [mode, setMode] = useState("existing"); // existing | new
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(initial?.client_id ? "existing" : "existing");
    setForm({
      client_id: initial?.client_id || "",
      service_id: initial?.service_id || services[0]?.id || "",
      staff_id: initial?.staff_id || staff[0]?.id || "",
      date: initial?.date || "",
      start: initial?.start || "09:00",
      status: initial?.status || "booked",
      notes: initial?.notes || "",
      is_walk_in: initial?.is_walk_in || false,
      client_first_name: "", client_last_name: "", client_mobile: "", client_email: "",
    });
  }, [open]); // eslint-disable-line

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const service = services.find((s) => s.id === form.service_id);
  const endMin = form.start && service ? (() => { const [h, m] = form.start.split(":").map(Number); const t = h * 60 + m + service.duration_min; return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; })() : "";

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (mode === "new") { payload.client_id = null; payload.is_walk_in = true; }
      if (isEdit) {
        await withOverride((force) => api.put(`/appointments/${initial.id}`, { service_id: form.service_id, staff_id: form.staff_id, date: form.date, start: form.start, status: form.status, notes: form.notes, force }));
      } else {
        await withOverride((force) => api.post("/appointments", { ...payload, force }));
      }
      toast.success(isEdit ? "Appointment updated" : "Appointment created");
      onSaved(); onClose();
    } catch (err) { if (!err.cancelled) toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const setStatus = async (status) => {
    try { await api.put(`/appointments/${initial.id}`, { status }); toast.success("Updated"); onSaved(); onClose(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this appointment?")) return;
    await api.delete(`/appointments/${initial.id}`); toast.success("Deleted"); onSaved(); onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg w-[calc(100vw-1.5rem)] max-h-[88vh] overflow-y-auto overflow-x-hidden rounded-2xl p-4 sm:p-6" data-testid="appointment-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Appointment" : "New appointment"}</DialogTitle></DialogHeader>
        <div className="space-y-4 min-w-0">
          {!isEdit && (
            <div>
              <div className="flex gap-2 mb-2">
                <button onClick={() => setMode("existing")} className={`text-xs px-3 py-1.5 rounded-full border ${mode === "existing" ? "bg-stone-900 text-white border-stone-900" : "border-stone-300"}`}>Existing client</button>
                <button onClick={() => setMode("new")} data-testid="appt-walkin-toggle" className={`text-xs px-3 py-1.5 rounded-full border ${mode === "new" ? "bg-stone-900 text-white border-stone-900" : "border-stone-300"}`}>Walk-in / new</button>
              </div>
              {mode === "existing" ? (
                <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
                  <SelectTrigger data-testid="appt-client-select" className="h-11 rounded-lg w-full min-w-0 [&>span]:truncate"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-2rem)]">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.mobile ? `· ${c.mobile}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input placeholder="First name" data-testid="appt-new-first" value={form.client_first_name} onChange={set("client_first_name")} className="h-10 rounded-lg" />
                  <Input placeholder="Last name" value={form.client_last_name} onChange={set("client_last_name")} className="h-10 rounded-lg" />
                  <Input placeholder="Mobile" data-testid="appt-new-mobile" value={form.client_mobile} onChange={set("client_mobile")} className="h-10 rounded-lg" />
                  <Input placeholder="Email (optional)" value={form.client_email} onChange={set("client_email")} className="h-10 rounded-lg" />
                </div>
              )}
            </div>
          )}
          <div><Label>Service</Label>
            <Select value={form.service_id} onValueChange={(v) => setForm((f) => ({ ...f, service_id: v }))}>
              <SelectTrigger data-testid="appt-service-select" className="mt-1.5 h-11 rounded-lg w-full min-w-0 [&>span]:truncate"><SelectValue placeholder="Select service" /></SelectTrigger>
              <SelectContent className="max-w-[calc(100vw-2rem)]">{services.map((s) => <SelectItem key={s.id} value={s.id} className="whitespace-normal">{s.name} · {GBP(s.price)} · {s.duration_min}m</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="min-w-0"><Label>Staff</Label>
              <Select value={form.staff_id} onValueChange={(v) => setForm((f) => ({ ...f, staff_id: v }))}>
                <SelectTrigger data-testid="appt-staff-select" className="mt-1.5 h-11 rounded-lg w-full min-w-0 [&>span]:truncate"><SelectValue /></SelectTrigger>
                <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="min-w-0"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="mt-1.5 h-11 rounded-lg w-full min-w-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="no_show">No-show</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="min-w-0"><Label>Date</Label><Input type="date" data-testid="appt-date" value={form.date} onChange={set("date")} className="mt-1.5 h-11 rounded-lg w-full min-w-0" /></div>
            <div className="min-w-0"><Label>Start</Label>
              <Input type="time" data-testid="appt-start" value={form.start} onChange={set("start")} className="mt-1.5 h-11 rounded-lg w-full min-w-0" />
              {endMin && <span className="text-xs text-stone-500 flex items-center gap-1 mt-1"><Clock className="h-3 w-3" />Ends {endMin}</span>}
            </div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} className="mt-1.5 rounded-lg" rows={2} /></div>

          {isEdit && (initial?.addons?.length > 0 || Object.keys(initial?.answers || {}).length > 0 || initial?.promo || initial?.price_type === "from") && (
            <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs space-y-1" data-testid="appt-booking-details">
              {initial.addons?.map((a) => <div key={a.id}>+ {a.name} ({GBP(a.price)})</div>)}
              {Object.entries(initial.answers || {}).map(([q, a]) => <div key={q}><span className="text-stone-500">{q}</span> <b>{a}</b></div>)}
              {initial.promo === "resident" && <div className="text-emerald-700">Didcot resident discount −{GBP(initial.discount)} ({initial.postcode})</div>}
              {initial.promo === "course" && <div className="text-emerald-700">Covered by prepaid course</div>}
              {initial.price_type === "from" && <div className="text-amber-700">"From" price — confirm the final price at checkout</div>}
            </div>
          )}
          {isEdit && (initial?.deposit > 0 || initial?.no_show_fee_status) && (
            <div className="flex flex-wrap gap-2 text-xs" data-testid="appt-payment-status">
              {initial?.deposit > 0 && (
                <span className={`px-2.5 py-1 rounded-full border ${initial?.deposit_paid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                  Deposit {GBP(initial.deposit)} · {initial?.deposit_paid ? "Paid" : "Unpaid"}
                </span>
              )}
              {initial?.no_show_fee_status && (
                <span className="px-2.5 py-1 rounded-full border bg-rose-50 text-rose-700 border-rose-200">
                  No-show fee {initial?.no_show_fee ? GBP(initial.no_show_fee) : ""} · {initial.no_show_fee_status}
                </span>
              )}
            </div>
          )}

          {isEdit && (
            <div className="flex flex-wrap gap-2 pt-1">
              {STATUS_ACTIONS.map((a) => (
                <button key={a.status} data-testid={`appt-status-${a.status}`} onClick={() => setStatus(a.status)}
                  className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1 ${a.cls}`}>
                  <a.icon className="h-3.5 w-3.5" />{a.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-3">
          {isEdit ? <Button variant="ghost" className="rounded-full text-rose-600" data-testid="appt-delete" onClick={remove}><Trash2 className="h-4 w-4 mr-1" /> Delete</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} className="rounded-full">Close</Button>
            <Button data-testid="appt-save-button" onClick={save} disabled={saving || (!isEdit && mode === "existing" && !form.client_id) || !form.service_id} className="rounded-full bg-stone-900 hover:bg-stone-800">Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
